import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings as SettingsIcon, Save, Store, QrCode } from 'lucide-react';
import { useToast, PageHeader, Button, Input } from '../components/ui';
import { initCurrency } from '../utils/currency';

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar', symbol: '$' },
  { value: 'PKR', label: 'Pakistani Rupee', symbol: 'Rs' },
];

const SettingsSection = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-6 max-w-lg">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={16} className="text-primary-600" />
      <h2 className="text-sm font-semibold text-neutral-800">{title}</h2>
    </div>
    {subtitle && <p className="text-xs text-neutral-500 mb-4">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    {children}
  </div>
);

const Settings = () => {
  const { toast } = useToast();
  const [currency, setCurrency] = useState('USD');
  const [business, setBusiness] = useState({ name: '', address: '', phone: '' });
  const [tableCap, setTableCap] = useState(5);
  const [loading, setLoading] = useState(true);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [savingCap, setSavingCap] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/api/config');
      setCurrency(res.data?.currency?.code || 'USD');
      setBusiness({
        name: res.data?.business?.name || '',
        address: res.data?.business?.address || '',
        phone: res.data?.business?.phone || '',
      });
      setTableCap(res.data?.publicOrderTableCap || 5);
    } catch (err) {
      toast.error('Could not load settings');
    } finally {
      setLoading(false);
    }
  };

  const authHeaders = { headers: { 'x-auth-token': localStorage.getItem('token') } };

  const handleSaveCurrency = async () => {
    setSavingCurrency(true);
    try {
      await axios.put('/api/config', { currencyCode: currency }, authHeaders);
      await initCurrency();
      toast.success(`Currency set to ${currency} — updated everywhere, no reload needed`);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error updating currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleSaveBusiness = async () => {
    setSavingBusiness(true);
    try {
      await axios.put('/api/config', {
        businessName: business.name,
        businessAddress: business.address,
        businessPhone: business.phone,
      }, authHeaders);
      toast.success('Business info updated — printed receipts now use this');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error updating business info');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveCap = async () => {
    setSavingCap(true);
    try {
      await axios.put('/api/config', { publicOrderTableCap: tableCap }, authHeaders);
      toast.success('Public ordering limit updated');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error updating limit');
    } finally {
      setSavingCap(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Settings" subtitle="System-wide preferences." />
        <p className="text-sm text-neutral-400">Loading…</p>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="System-wide preferences — changes apply immediately, no restart needed."
      />

      <div className="space-y-6">
        <SettingsSection
          icon={SettingsIcon}
          title="Currency"
          subtitle="Controls how every price is displayed — dashboard, menu, orders, receipts, analytics."
        >
          <div className="grid grid-cols-2 gap-3 mb-5">
            {CURRENCIES.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCurrency(c.value)}
                className={`border rounded-lg p-4 text-left transition-colors ${
                  currency === c.value
                    ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                <p className="text-lg font-bold text-neutral-900">{c.symbol}</p>
                <p className="text-sm font-medium text-neutral-700 mt-0.5">{c.label}</p>
                <p className="text-xs text-neutral-500">{c.value}</p>
              </button>
            ))}
          </div>
          <Button variant="primary" leftIcon={<Save size={16} />} loading={savingCurrency} onClick={handleSaveCurrency}>
            Save currency
          </Button>
        </SettingsSection>

        <SettingsSection
          icon={Store}
          title="Business info"
          subtitle="Shown on printed receipts and the customer QR ordering page."
        >
          <div className="space-y-3 mb-5">
            <Input
              label="Business name"
              type="text"
              placeholder="e.g. Chenab Restaurant"
              value={business.name}
              onChange={(e) => setBusiness({ ...business, name: e.target.value })}
            />
            <Input
              label="Address (optional)"
              type="text"
              placeholder="Street, city"
              value={business.address}
              onChange={(e) => setBusiness({ ...business, address: e.target.value })}
            />
            <Input
              label="Phone (optional)"
              type="text"
              placeholder="+92-..."
              value={business.phone}
              onChange={(e) => setBusiness({ ...business, phone: e.target.value })}
            />
          </div>
          <Button variant="primary" leftIcon={<Save size={16} />} loading={savingBusiness} onClick={handleSaveBusiness}>
            Save business info
          </Button>
        </SettingsSection>

        <SettingsSection
          icon={QrCode}
          title="Public QR ordering"
          subtitle="Caps how many unfinished orders a table can have open via the QR menu — stops a leaked link from being scripted into a flood of orders."
        >
          <div className="mb-5 max-w-[160px]">
            <Input
              label="Max open orders per table"
              type="number"
              min="1"
              value={tableCap}
              onChange={(e) => setTableCap(e.target.value)}
            />
          </div>
          <Button variant="primary" leftIcon={<Save size={16} />} loading={savingCap} onClick={handleSaveCap}>
            Save limit
          </Button>
        </SettingsSection>
      </div>
    </>
  );
};

export default Settings;
